import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, User, Menu, X } from "lucide-react";

const linkBase =
    "text-sm font-medium text-[var(--lt-muted)] hover:text-[var(--lt-text)] transition-colors";
const linkActive = "text-[var(--lt-text)]";

const NAV_ITEMS = [
    { to: "/", label: "Home", testid: "nav-home-link", end: true },
    { to: "/lessons", label: "Lessons", testid: "nav-lessons-link" },
    { to: "/practice", label: "Practice", testid: "nav-practice-link" },
    { to: "/test", label: "Test", testid: "nav-test-link" },
    { to: "/leaderboard", label: "Leaderboard", testid: "nav-leaderboard-link" },
    { to: "/utilities", label: "Utilities", testid: "nav-utilities-link" },
    { to: "/blog", label: "Blog", testid: "nav-blog-link" },
];

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const items = user
        ? [
              NAV_ITEMS[0],
              { to: "/dashboard", label: "Dashboard", testid: "nav-dashboard-link" },
              ...NAV_ITEMS.slice(1),
          ]
        : NAV_ITEMS;

    return (
        <header className="sticky top-0 z-40 glass">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
                <Link
                    to="/"
                    className="flex items-center gap-2 min-w-0"
                    data-testid="nav-landing-link"
                    onClick={() => setOpen(false)}
                >
                    <img src="/logo-96.png" alt="" width="32" height="32" className="h-8 w-8 shrink-0" data-testid="nav-logo" />
                    <span
                        className="text-xl sm:text-2xl font-semibold tracking-tight"
                        style={{
                            fontFamily:
                                '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif',
                        }}
                    >
                        <span className="text-[var(--lt-terracotta)]">Lipi</span>Typing
                    </span>
                    <span className="hidden sm:inline eyebrow ml-2 pl-3 border-l border-[var(--lt-border)]">
                        EN / ने
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-6 lg:gap-7">
                    {items.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.end}
                            className={({ isActive }) =>
                                `${linkBase} ${isActive ? linkActive : ""}`
                            }
                            data-testid={it.testid}
                        >
                            {it.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    {user ? (
                        <>
                            <Link
                                to="/profile"
                                className="hidden sm:flex items-center gap-2 text-sm font-medium text-[var(--lt-muted)] hover:text-[var(--lt-text)]"
                                data-testid="nav-profile-link"
                            >
                                <User size={16} />
                                <span className="hidden md:inline">{user.name}</span>
                            </Link>
                            <button
                                onClick={() => {
                                    logout();
                                    navigate("/");
                                }}
                                className="btn-ghost-line text-xs sm:text-sm inline-flex items-center gap-2"
                                data-testid="nav-logout-btn"
                            >
                                <LogOut size={14} />{" "}
                                <span className="hidden sm:inline">Logout</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                className="btn-ghost-line text-xs sm:text-sm px-3 sm:px-5"
                                data-testid="nav-login-button"
                            >
                                Login
                            </Link>
                            <Link
                                to="/register"
                                className="btn-terra text-xs sm:text-sm px-3 sm:px-5"
                                data-testid="nav-signup-button"
                            >
                                <span className="sm:hidden">Sign Up</span>
                                <span className="hidden sm:inline">Get Started</span>
                            </Link>
                        </>
                    )}
                    <button
                        onClick={() => setOpen((o) => !o)}
                        className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--lt-border)] text-[var(--lt-text)]"
                        aria-label="Toggle menu"
                        data-testid="nav-mobile-toggle"
                    >
                        {open ? <X size={16} /> : <Menu size={16} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu drawer */}
            {open && (
                <div
                    className="md:hidden border-t border-[var(--lt-border)] bg-[var(--lt-bg-2)]/95 backdrop-blur-md"
                    data-testid="nav-mobile-menu"
                >
                    <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col">
                        {items.map((it) => (
                            <NavLink
                                key={it.to}
                                to={it.to}
                                end={it.end}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `py-3 border-b border-[var(--lt-border)] last:border-0 text-base ${
                                        isActive
                                            ? "text-[var(--lt-terracotta)] font-semibold"
                                            : "text-[var(--lt-text)]"
                                    }`
                                }
                                data-testid={`${it.testid}-mobile`}
                            >
                                {it.label}
                            </NavLink>
                        ))}
                        {user && (
                            <NavLink
                                to="/profile"
                                onClick={() => setOpen(false)}
                                className="py-3 border-t border-[var(--lt-border)] text-base text-[var(--lt-text)] flex items-center gap-2"
                                data-testid="nav-profile-link-mobile"
                            >
                                <User size={16} /> {user.name}
                            </NavLink>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
